import IndustryLandingTemplate, { IndustryPageData } from '@/components/industry/IndustryLandingTemplate';

const restaurantsData: IndustryPageData = {
  slug: 'restaurants',
  name: 'Restaurants & Cafes',
  heroHeadline: 'Turn Every Happy Diner into a 5-Star Review',
  heroSubheadline: 'Help your restaurant or cafe build a strong Google presence with AI-generated review suggestions that capture the dining experience.',
  painPoint: '94% of diners check Google reviews before choosing a restaurant',
  trustProblem: 'A single negative review can cost you 30 customers. Most restaurants don\'t have a review collection strategy.',
  challenges: [
    {
      title: 'Review-Rating Gap',
      description: 'You serve 100+ happy customers daily, but only 1-2 leave reviews. Meanwhile, unhappy customers always review.',
    },
    {
      title: 'Invisible to Food Searches',
      description: 'When people search "best biryani in Hyderabad" or "cafe near me", your restaurant doesn\'t show up.',
    },
    {
      title: 'Generic Food Reviews',
      description: 'Reviews saying "food was good" don\'t help you rank for specific dishes or cuisine types.',
    },
    {
      title: 'Seasonal Competition',
      description: 'During festivals and weekends, restaurants with better ratings capture all the foot traffic.',
    },
  ],
  useCases: [
    {
      category: 'Cuisine & Dishes',
      description: 'Reviews highlighting signature dishes and cuisine specialties.',
      keywords: ['best biryani', 'authentic cuisine', 'must-try dish'],
    },
    {
      category: 'Ambience & Service',
      description: 'Reviews focusing on dining experience and customer service.',
      keywords: ['great ambience', 'friendly staff', 'perfect for date night'],
    },
    {
      category: 'Location & Occasions',
      description: 'Reviews that boost local visibility and occasion-based searches.',
      keywords: ['restaurant in Koramangala', 'birthday celebration', 'family dinner'],
    },
  ],
  caseStudy: {
    businessType: 'North Indian Restaurant',
    city: 'Delhi',
    challenges: [
      'Stuck at 3.8 rating with 180 reviews for 2 years',
      'Not appearing in "butter chicken Delhi" or "family restaurant" searches',
      'Zomato/Swiggy taking most of the traffic instead of direct walk-ins',
    ],
    solution: 'Implemented Review Plum with categories for Signature Dishes, Family Dining, and Party Orders. Created QR codes for tables that lead to pre-written reviews.',
    results: [
      { metric: 'Google Reviews', value: '180 → 450' },
      { metric: 'Google Rating', value: '3.8 → 4.5' },
      { metric: 'Direct Walk-ins', value: '+55%' },
      { metric: '"Near Me" Rankings', value: 'Top 5' },
    ],
  },
  sampleReviews: [
    {
      category: 'Cuisine & Dishes',
      reviews: [
        {
          text: 'Hands down the best butter chicken I\'ve had in Delhi! The gravy was rich and creamy, and the naan was perfectly fresh from the tandoor. This restaurant in Karol Bagh is a hidden gem for authentic North Indian cuisine. The portions are generous and worth every rupee.',
          keywords: ['best butter chicken in Delhi', 'North Indian cuisine', 'restaurant in Karol Bagh'],
        },
        {
          text: 'Amazing biryani experience! The hyderabadi dum biryani was perfectly spiced with tender meat pieces. We also tried the paneer tikka which was equally delicious. Great family restaurant for lunch in South Delhi. Will definitely come back for more!',
          keywords: ['biryani', 'family restaurant', 'South Delhi'],
        },
      ],
    },
    {
      category: 'Ambience',
      reviews: [
        {
          text: 'Perfect restaurant for a romantic date night! The rooftop seating with city lights view was magical. Live music on weekends adds to the experience. The dim lighting and elegant decor create an amazing atmosphere. Best fine dining ambience in Connaught Place.',
          keywords: ['date night restaurant', 'rooftop dining', 'fine dining Connaught Place'],
        },
        {
          text: 'Celebrated my anniversary here and the staff made it extra special. They decorated our table beautifully. The private dining area is perfect for family celebrations. Amazing restaurant for special occasions in South Delhi.',
          keywords: ['anniversary celebration', 'private dining', 'special occasions South Delhi'],
        },
      ],
    },
    {
      category: 'Delivery',
      reviews: [
        {
          text: 'Ordered delivery for a house party and everything arrived hot and fresh! The packaging was excellent and the food tasted as good as dining in. Quick delivery within 30 minutes. Best restaurant for home delivery in Delhi NCR.',
          keywords: ['food delivery', 'home delivery Delhi NCR', 'party order'],
        },
        {
          text: 'Regular customer for their lunch delivery. The portion sizes are generous and prices are reasonable. The delivery guy is always on time and polite. Best option for office lunch delivery in Gurgaon.',
          keywords: ['lunch delivery', 'office food Gurgaon', 'regular delivery'],
        },
      ],
    },
  ],
  cityKeywords: [
    'restaurant in Mumbai',
    'cafe Delhi',
    'best food Bengaluru',
    'dine out Chennai',
    'eating places Pune',
    'food court Hyderabad',
  ],
  nearMeKeywords: [
    'restaurant near me',
    'cafe near me',
    'food near me',
    'best restaurant nearby',
    'dinner place close by',
    'lunch spots near me',
  ],
  ctaText: 'Boost Your Restaurant Reviews',
};

export default function RestaurantsPage() {
  return <IndustryLandingTemplate data={restaurantsData} />;
}
